"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { SmartDateInput } from "@/components/ui/smart-date-input";
import { formatDate } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { useCarePlanContext } from "./care-plan";
import { DurationPopoverContent } from "./duration-connector";
import { formatMinutes } from "./step-detail-helpers";
import { type CarePlanStep, type PatientCarePlanStep, StepType, findBranchInTree, isLinkedMessage, isPatientStep } from "./types";
import { getShortestBranchDurationMinutes } from "./use-patient-care-plan";
type DateMode = "exact" | "relative";

/**
 * Get the effective end date of the previous step, accounting for
 * decision step branch durations. This mirrors recalculatePositions
 * which advances prevTimelineDate past the shortest branch.
 */
function getEffectivePrevDate(prevStep: PatientCarePlanStep): Date {
  const baseDate = new Date(prevStep.scheduledDate);
  if (prevStep.type === StepType.DECISION) {
    const branchDuration = getShortestBranchDurationMinutes(prevStep);
    return new Date(baseDate.getTime() + branchDuration * 60000);
  }
  return baseDate;
}

/**
 * Check whether this step is the first non-message step in its timeline
 * (main steps list or branch).
 */
function isFirstInTimeline(step: CarePlanStep, allSteps: CarePlanStep[]): boolean {
  const timeline = (list: CarePlanStep[]) => list.filter(s => !isLinkedMessage(s));
  if (step.branchId) {
    const result = findBranchInTree(allSteps, step.branchId);
    if (result) {
      const branchTimeline = timeline(result.branch.steps);
      return branchTimeline[0]?.id === step.id;
    }
  }
  const mainTimeline = timeline(allSteps);
  return mainTimeline[0]?.id === step.id;
}
export function StepDate() {
  const $ = _c(54);
  const {
    state: t0,
    actions: t1,
    config: t2
  } = useCarePlanContext();
  const {
    steps,
    selectedStep
  } = t0;
  const {
    updateStep,
    updateBranchStep
  } = t1;
  const {
    readOnly
  } = t2;
  const isPatient = selectedStep ? isPatientStep(selectedStep) : false;
  const isFirst = selectedStep ? isFirstInTimeline(selectedStep, steps) : false;
  const defaultMode = isPatient && isFirst ? "exact" : "relative";
  const [mode, setMode] = useState(defaultMode);
  let t3;
  if ($[0] !== defaultMode) {
    t3 = () => {
      setMode(defaultMode);
    };
    $[0] = defaultMode;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = selectedStep?.id;
  let t5;
  if ($[2] !== t4) {
    t5 = [t4];
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  useEffect(t3, t5);
  if (!selectedStep) {
    return null;
  }
  if (!isPatient) {
    if (isFirst) {
      return null;
    }
    let t6;
    if ($[4] !== selectedStep.branchId || $[5] !== selectedStep.id || $[6] !== updateBranchStep || $[7] !== updateStep) {
      t6 = minutes => {
        if (minutes == null) {
          return;
        }
        if (selectedStep.branchId) {
          updateBranchStep(selectedStep.branchId, selectedStep.id, {
            minutesAfterPrevious: minutes
          });
        } else {
          updateStep(selectedStep.id, {
            minutesAfterPrevious: minutes
          });
        }
      };
      $[4] = selectedStep.branchId;
      $[5] = selectedStep.id;
      $[6] = updateBranchStep;
      $[7] = updateStep;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    const handleTemplateChange = t6;
    let t7;
    if ($[9] !== handleTemplateChange || $[10] !== readOnly || $[11] !== selectedStep.minutesAfterPrevious) {
      t7 = <div className="space-y-1">{readOnly ? <p className="text-body text-foreground-secondary">{selectedStep.minutesAfterPrevious === 0 ? "Immediately after previous step" : selectedStep.minutesAfterPrevious ? `${formatMinutes(selectedStep.minutesAfterPrevious)} after previous step` : "Not set"}</p> : <DurationPopoverContent value={selectedStep.minutesAfterPrevious} onChange={handleTemplateChange} />}</div>;
      $[9] = handleTemplateChange;
      $[10] = readOnly;
      $[11] = selectedStep.minutesAfterPrevious;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    return t7;
  }
  if (!isPatientStep(selectedStep)) {
    return null;
  }
  let t6;
  if ($[13] !== selectedStep.branchId || $[14] !== selectedStep.id || $[15] !== steps) {
    bb0: {
      const timelineOf = _temp2;
      let t7;
      if ($[17] !== selectedStep.id) {
        t7 = timeline => {
          const idx = timeline.findIndex(s_0 => s_0.id === selectedStep.id);
          return idx > 0 ? timeline[idx - 1] : null;
        };
        $[17] = selectedStep.id;
        $[18] = t7;
      } else {
        t7 = $[18];
      }
      const findPrev = t7;
      if (selectedStep.branchId) {
        const result = findBranchInTree(steps, selectedStep.branchId);
        if (!result) {
          t6 = null;
          break bb0;
        }
        const prev = findPrev(timelineOf(result.branch.steps));
        if (prev) {
          t6 = prev;
          break bb0;
        }
        t6 = isPatientStep(result.decisionStep) ? result.decisionStep : null;
        break bb0;
      }
      let t8;
      if ($[19] !== findPrev || $[20] !== steps) {
        t8 = findPrev(timelineOf(steps));
        $[19] = findPrev;
        $[20] = steps;
        $[21] = t8;
      } else {
        t8 = $[21];
      }
      t6 = t8;
    }
    $[13] = selectedStep.branchId;
    $[14] = selectedStep.id;
    $[15] = steps;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const prevStep = t6;
  let t7;
  if ($[22] !== mode || $[23] !== prevStep || $[24] !== readOnly || $[25] !== selectedStep.branchId || $[26] !== selectedStep.completedDate || $[27] !== selectedStep.id || $[28] !== selectedStep.isDateEstimated || $[29] !== selectedStep.minutesAfterPrevious || $[30] !== selectedStep.scheduledDate || $[31] !== updateBranchStep || $[32] !== updateStep) {
    const displayDate = selectedStep.completedDate ? new Date(selectedStep.completedDate) : new Date(selectedStep.scheduledDate);
    let t8;
    if ($[34] !== selectedStep.scheduledDate) {
      t8 = new Date(selectedStep.scheduledDate);
      $[34] = selectedStep.scheduledDate;
      $[35] = t8;
    } else {
      t8 = $[35];
    }
    const scheduledDate = t8;
    let t9;
    if ($[36] !== selectedStep.branchId || $[37] !== selectedStep.id || $[38] !== updateBranchStep || $[39] !== updateStep) {
      t9 = updates => {
        if (selectedStep.branchId) {
          updateBranchStep(selectedStep.branchId, selectedStep.id, updates);
        } else {
          updateStep(selectedStep.id, updates);
        }
      };
      $[36] = selectedStep.branchId;
      $[37] = selectedStep.id;
      $[38] = updateBranchStep;
      $[39] = updateStep;
      $[40] = t9;
    } else {
      t9 = $[40];
    }
    const applyUpdate = t9;
    let t10;
    if ($[41] !== applyUpdate || $[42] !== prevStep || $[43] !== selectedStep.scheduledDate) {
      t10 = date => {
        if (!date) {
          return;
        }
        const updated = new Date(selectedStep.scheduledDate);
        updated.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
        applyUpdate({
          scheduledDate: updated.toISOString(),
          minutesAfterPrevious: prevStep ? Math.round((updated.getTime() - getEffectivePrevDate(prevStep).getTime()) / 60000) : undefined
        });
      };
      $[41] = applyUpdate;
      $[42] = prevStep;
      $[43] = selectedStep.scheduledDate;
      $[44] = t10;
    } else {
      t10 = $[44];
    }
    const handleExactDateChange = t10;
    let t11;
    if ($[45] !== applyUpdate || $[46] !== prevStep) {
      t11 = minutes_0 => {
        if (minutes_0 == null || !prevStep) {
          return;
        }
        const prevDate = getEffectivePrevDate(prevStep);
        const newDate = new Date(prevDate.getTime() + minutes_0 * 60 * 1000);
        applyUpdate({
          scheduledDate: newDate.toISOString(),
          minutesAfterPrevious: minutes_0
        });
      };
      $[45] = applyUpdate;
      $[46] = prevStep;
      $[47] = t11;
    } else {
      t11 = $[47];
    }
    const handleRelativeChange = t11;
    let t12;
    if ($[48] !== mode || $[49] !== prevStep || $[50] !== readOnly) {
      t12 = prevStep && !readOnly && <div className="flex items-center gap-0.5 rounded-md border border-border bg-background-secondary p-0.5 text-caption"><button type="button" className={cx("rounded px-1.5 py-0.5 transition-colors", mode === "exact" ? "bg-background text-foreground shadow-sm" : "text-foreground-secondary hover:text-foreground")} onClick={() => setMode("exact")}>Exact</button><button type="button" className={cx("rounded px-1.5 py-0.5 transition-colors", mode === "relative" ? "bg-background text-foreground shadow-sm" : "text-foreground-secondary hover:text-foreground")} onClick={() => setMode("relative")}>Relative</button></div>;
      $[48] = mode;
      $[49] = prevStep;
      $[50] = readOnly;
      $[51] = t12;
    } else {
      t12 = $[51];
    }
    let t13;
    if ($[52] !== t12) {
      t13 = <div className="flex items-center justify-between">{t12}</div>;
      $[52] = t12;
      $[53] = t13;
    } else {
      t13 = $[53];
    }
    t7 = <div className="space-y-1">{t13}{mode === "exact" || !prevStep ? readOnly ? <p className="text-body text-foreground-secondary"><span>{selectedStep.isDateEstimated ? "~" : ""}</span><span>{!!selectedStep.completedDate && "Completed: "}</span>{formatDate(displayDate)}</p> : <SmartDateInput value={scheduledDate} onChange={handleExactDateChange} direction="future" placeholder="today, tomorrow, next week, mar 15..." /> : readOnly ? <p className="text-body text-foreground-secondary">{selectedStep.minutesAfterPrevious === 0 ? "Immediately after previous step" : selectedStep.minutesAfterPrevious ? `${formatMinutes(selectedStep.minutesAfterPrevious)} after previous step` : "Not set"}</p> : <DurationPopoverContent value={selectedStep.minutesAfterPrevious} onChange={handleRelativeChange} />}</div>;
    $[22] = mode;
    $[23] = prevStep;
    $[24] = readOnly;
    $[25] = selectedStep.branchId;
    $[26] = selectedStep.completedDate;
    $[27] = selectedStep.id;
    $[28] = selectedStep.isDateEstimated;
    $[29] = selectedStep.minutesAfterPrevious;
    $[30] = selectedStep.scheduledDate;
    $[31] = updateBranchStep;
    $[32] = updateStep;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  return t7;
}
function _temp2(list) {
  return list.filter(_temp);
}
function _temp(s) {
  return !isLinkedMessage(s) && isPatientStep(s);
}